import { motion } from "framer-motion";
import { ABOUT_PARAS, STATS, OFFERINGS, IMAGES } from "../../data";
import { Check } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="max-w-[1600px] mx-auto px-6 md:px-12 py-28 md:py-40"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left — heading + image */}
        <div className="lg:col-span-5">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-sans-lux text-xs uppercase tracking-[0.3em] text-[#D49A44] mb-6"
          >
            The House
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-lux font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#F4EFE6] mb-10"
          >
            Purveyors of the <span className="italic text-[#D49A44]">rare</span> &amp; the <span className="italic text-[#D49A44]">refined.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative overflow-hidden border border-[#2C2621] h-[300px] md:h-[380px] group"
          >
            <img
              src={IMAGES.heritageBuilding}
              alt="The heritage house"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#12100E]/80 to-transparent" />
          </motion.div>
        </div>

        {/* Right — copy + offerings */}
        <div className="lg:col-span-7 lg:pl-12 flex flex-col justify-center">
          <div className="space-y-7">
            {ABOUT_PARAS.map((p, i) => (
              <motion.p
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className={`font-sans-lux leading-relaxed text-[#B0A89D] ${
                  i === 0 ? "text-lg md:text-xl text-[#F4EFE6]/90" : "text-base"
                }`}
              >
                {p}
              </motion.p>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4"
          >
            {OFFERINGS.map((o) => (
              <div key={o} className="flex items-center gap-3 border-b border-[#2C2621] pb-4">
                <Check size={16} className="text-[#D49A44] shrink-0" />
                <span className="font-sans-lux text-sm text-[#F4EFE6]">{o}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Stats band */}
      <div className="mt-24 grid grid-cols-2 lg:grid-cols-4 border-t border-[#2C2621]">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            custom={i}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="py-10 px-2 lg:px-8 border-b lg:border-b-0 border-r border-[#2C2621] last:border-r-0 [&:nth-child(2)]:border-r-0 lg:[&:nth-child(2)]:border-r"
          >
            <p className="font-serif-lux text-5xl md:text-6xl font-light text-[#D49A44] leading-none mb-4">
              {s.value}
            </p>
            <p className="font-sans-lux text-[11px] uppercase tracking-[0.2em] text-[#7C756B]">
              {s.label}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
